import sageIcon from '~/assets/public/sage-intacct-icon.svg';
import { Icon, InlineTooltip, Tooltip } from '~/components';
import { useApi, useConfirmation, useIntegrations, useSubscription, useWorkspace } from '~/contexts';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { colors, weights } from '~/styles';
import RemoveSageConfirmation from './RemoveSageConfirmation';
import SageSetupModal from './SageSetupModal';
import { useFeatures } from '~/hooks';

const Item = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 10.25rem;
  height: 9.375rem;
  margin: 1.25rem 0.625rem;
  background-color: ${colors.white};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  border: solid 0.125rem ${colors.white};
  border-radius: 0.3125rem;
  overflow: hidden;
`;

const Description = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 50%;
`;

const Name = styled.p`
  margin-top: 0.75rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  min-height: 1.25rem;

  max-width: 9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  height: 2rem;
  background-color: ${colors.grey10};
`;

const ActionStyle = css`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 2rem;
  padding: 0;
  color: ${colors.black};
  font-size: 0.75rem;
  font-weight: ${weights.normal};
  background-color: ${colors.grey5};
  border-radius: 0;

  &:not(:first-child) {
    margin-left: 1px;
  }

  &:hover {
    color: ${colors.black};
    background-color: ${colors.grey10};
  }
`;

const ActionAnchor = styled.a`
  ${ActionStyle}
`;

const ActionButton = styled.button`
  ${ActionStyle}

  position: relative;

  &:disabled {
    box-shadow: none;
    opacity: 0.5;

    &:hover {
      cursor: not-allowed;
    }
  }
`;

const WarningTooltip = styled(Tooltip)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
`;

function SageIntegration({ integration, onChange }) {
  const history = useHistory();
  const { url } = useRouteMatch();
  const confirmation = useConfirmation();
  const { notify } = useSubscription();
  const [sageConnection, setSageConnection] = useState({ data: null, isReady: false });
  const [showSetup, setShowSetup] = useState(false);
  const { workspace, refreshWorkspace } = useWorkspace();
  const api = useApi();
  const integrations = useIntegrations();
  const features = useFeatures();

  const fetchSageConnection = useCallback(async () => {
    if (!integration.isConnected) return;
    try {
      const { data } = await api.www.workspaces(workspace.id).sage(integration.sageConnectionId).getStatus();
      setSageConnection({ data, isReady: true });
    } catch {
      setSageConnection({ data: null, isReady: true });
    }
  }, [api, integration, workspace.id]);

  useEffect(() => {
    fetchSageConnection();
  }, [fetchSageConnection]);

  const handleFinish = (connectionId) => {
    setShowSetup(false);
    notify(useSubscription.keys.integrations_changed);
    refreshWorkspace();
    if (typeof onChange === 'function') onChange();
    history.push(`${url}/sage/${connectionId}?action=setup`);
  };

  const handleRemove = () => {
    confirmation.prompt((resolve) => (
      <RemoveSageConfirmation
        sageConnectionId={integration.sageConnectionId}
        sageConnectionName={integration.connectionName}
        onClose={resolve}
        onRemove={() => {
          if (typeof onChange === 'function') {
            onChange();
          }
          notify(useSubscription.keys.integrations_changed);
          refreshWorkspace();
          resolve(true);
        }}
      />
    ));
  };

  const otherAccountingActive = (integrations.qbo || integrations.xero) && !features.multipleAccountingIntegrations;

  return (
    <Item>
      <Description>
        <Image src={sageIcon} />
        <Name>{integration.connectionName ?? 'Sage Intacct'}</Name>
        {sageConnection.isReady && sageConnection.data?.status !== 'ok' && (
          <WarningTooltip message={sageConnection.data?.message}>
            <Icon icon="warning" color={colors.warning} />
          </WarningTooltip>
        )}
      </Description>
      {integration.isConnected ? (
        <Actions>
          <ActionButton onClick={() => history.push(`${url}/sage/${integration.sageConnectionId}`)}>
            Settings
          </ActionButton>
          <ActionButton onClick={handleRemove}>Remove</ActionButton>
        </Actions>
      ) : (
        <Actions>
          <ActionAnchor
            href="https://help.ruddr.io/integrations/sage-intacct-integration-overview"
            target="_blank"
            rel="noopener noreferrer">
            Details
          </ActionAnchor>
          {otherAccountingActive ? (
            <ActionButton disabled>
              <InlineTooltip message={<>Remove other accounting integrations to install Sage Intacct.</>} />
              Install
            </ActionButton>
          ) : (
            <ActionButton onClick={() => setShowSetup(true)}>Install</ActionButton>
          )}
        </Actions>
      )}

      {showSetup && <SageSetupModal onClose={() => setShowSetup(false)} onFinish={handleFinish} />}
    </Item>
  );
}

export default SageIntegration;
